import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-skeleton-block>` says about itself.
 *
 * ── THERE IS NO SPINNER YOU CAN PLACE, AND THAT IS DELIBERATE ────────────────
 * Somebody looking for one will look here first, so this is the file that has to answer it. Busy is
 * a state OF something: either of the control that is doing the work — which is what `Button`'s
 * loading state is — or of the region that is filling, which is this. A placeholder that keeps the
 * shape of the content that is coming beats a mark drawn over the top of it, because the layout
 * does not move when the data lands. A free-floating spinner belongs to neither the control nor the
 * region and moves everything when it goes away. Nothing about that has changed: there is still no
 * mark in the palette for an author to drop onto a page, and there is not going to be one.
 *
 * CAOS-610 added the THIRD case, which is neither of the two above. A route landed on from a link —
 * an email verification, a magic-link sign-in — has no control, because nobody clicked one, and no
 * region of a known shape, because the page has not decided what it is yet. The thing that is busy
 * is the PAGE, and `PagePending` is how a page or route says so. It declares NO placement, so it
 * stays out of the palette exactly as this note requires; a page renders it in code. If you came
 * here looking for a spinner to place, the answer is still no — but if the page itself is what is
 * waiting, that is the piece.
 *
 * ── `variant` IS AN INPUT THAT IS NOT OBSERVED ───────────────────────────────
 * `observedAttributes` is `w`, `h` and `radius` — the three the class reads in script. `variant` is
 * read by the STYLESHEET, through `:host([variant="…"])` selectors, so it needs no callback to take
 * effect and is a real input all the same. Reading the observed list alone would have missed it.
 *
 * ── ONE NOTE ON THE OLD EXAMPLE ──────────────────────────────────────────────
 * The hand-kept sample this replaces drew `variant="text"`. There is no `text` variant — the four
 * are line, title, block and circle — so that block silently fell back to the default line shape.
 * The variants example below draws the four that exist.
 */
const docs: ComponentDocs = {
  aliases: ['skeleton', 'shimmer', 'placeholder', 'ghost content', 'loading state', 'spinner', 'loader'],

  answers: [
    'show that a region is still loading',
    'stop the page jumping when the data arrives',
    'stand in for content whose shape is already known',
    'show a list or a card is filling without covering it up',
  ],

  placements: ['recordPage', 'homePage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'variant',
      attribute: 'variant',
      type: 'text',
      options: ['line', 'title', 'block', 'circle'],
      description:
        'Which preset shape to draw: line (a line of text), title (a taller, shorter line), block ' +
        '(a filled region) or circle (an avatar). Read by the stylesheet rather than by script.',
      default: 'line',
    },
    {
      name: 'w',
      attribute: 'w',
      type: 'text',
      description:
        'Override the width with any CSS length or percentage. Varying it down a stack is what makes ' +
        'a run of lines read as text rather than as bars.',
    },
    {
      name: 'h',
      attribute: 'h',
      type: 'text',
      description: "Override the height with any CSS length. Use it to match the real content's height exactly.",
    },
    {
      name: 'radius',
      attribute: 'radius',
      type: 'text',
      description:
        'Override the corner radius with any CSS length. Set it when the thing being stood in for ' +
        'has corners the variant presets do not match.',
    },
  ],

  // Declared as NONE rather than left unsaid: a placeholder is drawn, watched and replaced — there
  // is nothing for it to report, and nothing about its lifetime it would know before its host does.
  events: [],

  // NONE: the block renders a single div and no <slot>. Content cannot be put inside a placeholder,
  // which is the point of one.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-surface-2', controls: 'The base tint of the block — the colour it is when it is not shimmering.' },
    { token: '--caos-color-surface', controls: 'The colour of the highlight that sweeps across it.' },
    { token: '--caos-radius-sm', controls: 'The corners of the line and title variants.' },
    { token: '--caos-radius', controls: 'The corners of the block variant.' },
    { token: '--caos-radius-pill', controls: 'The corners of the circle variant — what makes it round.' },
  ],

  parts: [
    { part: 'block', is: 'the shimmering block' },
  ],

  methods: [],

  placing:
    'A placeholder is not laid out on its own: it goes WHERE THE CONTENT WILL GO, inside the same ' +
    'region, at the same size, and is replaced by the real thing. Several of them arranged the way ' +
    'the content is arranged is the normal case — one on its own says almost nothing. A component ' +
    'that loads its own data usually draws these itself rather than being handed them: DataTable ' +
    'builds a grid of them behind its loading flag.',

  whenToUse:
    'A REGION is waiting for data and its shape is already known. Reach for it instead of a spinner ' +
    'or an empty box: the placeholder holds the layout still, so nothing moves when the content ' +
    'lands. If what is busy is a control rather than a region, the busy state belongs on the control.',

  insteadUse: [
    {
      name: 'Button',
      when:
        'The busy thing is a CONTROL — a save in flight. Its loading state draws the spinner, marks ' +
        'the control busy and stops it being pressed twice, which a placeholder beside it cannot do.',
    },
    {
      name: 'ProgressBar',
      when: 'How far along the work is can actually be measured, or it runs in named stages.',
    },
    {
      name: 'EmptyState',
      when: 'The wait is over and the answer was nothing. A placeholder that never resolves reads as a hang.',
    },
    {
      name: 'PagePending',
      when:
        'The PAGE is what is waiting — a route landed on from a link, before it knows what it is ' +
        'going to be. There is no shape to preserve yet, so there is nothing for a placeholder to ' +
        'stand in for.',
    },
  ],

  accessibility:
    'The block is marked aria-hidden and the host element is marked aria-busy when it connects, so ' +
    'a screen reader is not read a wall of meaningless boxes. The shimmer honours ' +
    'prefers-reduced-motion: for a reader who has opted out it is a flat tint with no sweep at all. ' +
    'Two things are left to the author. The aria-busy is only applied when the element carries no ' +
    'role attribute of its own, so setting a role suppresses it. And busy is announced per ' +
    'placeholder, not per region — a region that needs to announce that it is loading, and again ' +
    'that it has loaded, needs a live region around it.',

  examples: [
    {
      key: 'region',
      label: 'A section keeping its shape',
      summary:
        'A record section mid-load: the heading and the field labels are already known, so only the ' +
        'values stand in — at the widths the real values will occupy, in the rows they will occupy, ' +
        'so nothing moves when they land.',
      placement: {
        component: 'caos-detail-section',
        inputs: { heading: 'Account information' },
        children: [
          {
            component: 'caos-key-value-row',
            inputs: { label: 'Region' },
            children: [{ component: 'caos-skeleton-block', inputs: { w: '68px', h: '13px' } }],
          },
          {
            component: 'caos-key-value-row',
            inputs: { label: 'Status' },
            children: [{ component: 'caos-skeleton-block', inputs: { w: '52px', h: '13px' } }],
          },
          {
            component: 'caos-key-value-row',
            inputs: { label: 'Owner' },
            children: [{ component: 'caos-skeleton-block', inputs: { w: '96px', h: '13px' } }],
          },
          {
            component: 'caos-key-value-row',
            inputs: { label: 'Name' },
            children: [{ component: 'caos-skeleton-block', inputs: { w: '124px', h: '13px' } }],
          },
        ],
      },
    },
    {
      key: 'variants',
      label: 'The four shapes',
      summary:
        'Every preset the block has: a heading line, a text line, a filled region and an avatar ' +
        'circle — the four to pick between when matching the content that is coming.',
      // STACKED, not in a row, and that is a fact about the piece rather than a layout preference.
      // A line and a title fill the width they are given, because the text they stand in for does.
      // Side by side in a row they have no width to fill and draw nothing at all — which is what
      // this example did until it was looked at in a browser, where two of the four shapes it
      // promised were invisible.
      stage: { arrange: 'column' },
      placement: [
        { component: 'caos-skeleton-block', inputs: { variant: 'title' } },
        { component: 'caos-skeleton-block', inputs: { variant: 'line' } },
        { component: 'caos-skeleton-block', inputs: { variant: 'block', w: '160px' } },
        { component: 'caos-skeleton-block', inputs: { variant: 'circle' } },
      ],
    },
  ],
};

export default docs;
